import { type Brand } from '../entity/api/brand';
import { type OrganizationUser, OrganizationUserRole } from '../entity/api/organization-user';
import { trackError } from '../error/tracking';
import { captureException } from '../logger/capture-exception';
import { getQueryClient } from '../sdk/request';
import { groupPostHogWorkspace } from '../services/posthog';
import {
  fetchBrandsListImperative,
  getBrandsByOrgIdFromCache,
} from '../services/storage/brands-queries';
import { fetchOrganizationBillingImperative } from '../services/storage/organization-billing-queries';
import {
  fetchUserOrganizationsImperative,
  getAllOrganizationUsersFromCache,
} from '../services/storage/organization-users-queries';
import {
  fetchOrganizationsListImperative,
  getOrganizationFromCache,
  getOrganizationsListFromCache,
} from '../services/storage/organizations-queries';
import {
  fetchAuthUserImperative,
  getAuthUserFromCache,
  updateUserImperative,
} from '../services/storage/users-queries';
import { changeLanguage, tGlobal } from '../services/translation';
import { getSelectedBrand } from './brand';
import { getSelectedOrganization } from './organization';

async function setCurrentOrgAndBrand(
  preferredOrgId?: string | null,
  preferredBrandId?: string | null
) {
  const queryClient = getQueryClient();

  // Fetch auth user via TanStack Query
  const currentUser = await fetchAuthUserImperative(queryClient);
  if (currentUser == null) {
    return;
  }

  // Get all organizations from cache (should be populated by fetchOrganizationsListImperative)
  const organizations = getOrganizationsListFromCache(queryClient) ?? [];
  if (organizations.length === 0) {
    return;
  }

  // First check if we already have a current organization
  const currentOrg = getSelectedOrganization(currentUser);
  const currentBrand = getSelectedBrand(currentUser);
  const allOrgUsers = getAllOrganizationUsersFromCache(queryClient);
  const orgUser = allOrgUsers.filter(function (user): user is OrganizationUser {
    return user != null && user?.userId === currentUser._id;
  });

  if (orgUser.length === 0) {
    return;
  }

  // If user has a selected organization ID, use it
  let orgId = '';
  if (
    orgUser.some(function (orgUser) {
      return (
        orgUser.organizationId === currentUser.selectedOrganizationId &&
        (orgUser.role === OrganizationUserRole.Owner ||
          orgUser.role === OrganizationUserRole.Member)
      );
    })
  ) {
    orgId = currentUser.selectedOrganizationId ?? '';
  }

  // If no selected organization ID or it doesn't exist, fall back to current org
  if (orgId === '' || !organizations.some((org) => org._id === orgId)) {
    orgId = currentOrg?._id ?? '';
  }

  // If still no valid org ID, use preferred org or first org
  if (orgId === '' || !organizations.some((org) => org._id === orgId)) {
    const hasValidPreferredOrg =
      typeof preferredOrgId === 'string' &&
      preferredOrgId !== '' &&
      organizations.some((org) => org._id === preferredOrgId);

    orgId = hasValidPreferredOrg ? preferredOrgId : organizations[0]._id;
  }

  // Get brands for the selected organization from cache
  const brands = (getBrandsByOrgIdFromCache(queryClient, orgId) ?? []).filter(
    function (brand): brand is Brand {
      return brand != null && brand.organizationId === orgId;
    }
  );

  // If user has a selected brand ID, use it
  let brandId = currentUser?.selectedBrandId ?? '';

  // If selected brand doesn't exist or doesn't belong to selected org, fall back to current brand
  if (
    brandId === '' ||
    !brands.some((brand) => brand._id === brandId && brand.organizationId === orgId)
  ) {
    brandId = currentBrand?._id ?? '';
  }

  // If still no valid brand ID, use preferred brand or first brand
  if (brandId === '' || !brands.some((brand) => brand._id === brandId)) {
    const hasValidPreferredBrand =
      typeof preferredBrandId === 'string' &&
      preferredBrandId !== '' &&
      brands.some((brand) => brand._id === preferredBrandId);

    brandId = hasValidPreferredBrand ? preferredBrandId : (brands[0]?._id ?? '');
  }

  // Set the values via TanStack Query
  await updateUserImperative(queryClient, {
    selectedOrganizationId: orgId,
    selectedBrandId: brandId,
  });
}

export function updateAuthStorageData(orgId?: string | null) {
  const queryClient = getQueryClient();

  return Promise.resolve()
    .then(async function () {
      const newUser = await fetchAuthUserImperative(queryClient);
      if (newUser != null) {
        await changeLanguage(newUser.language ?? 'en');
      }
    })
    .then(function () {
      return Promise.all([
        fetchOrganizationsListImperative(queryClient),
        fetchBrandsListImperative(queryClient),
        fetchUserOrganizationsImperative(queryClient),
      ]);
    })
    .then(() => setCurrentOrgAndBrand(orgId))
    .then(function () {
      // Fire-and-forget: PostHog grouping must never block or reject the invite flow.
      // The billing fetch is only needed for analytics; errors are captured separately.
      void (async function () {
        try {
          const updatedUser = getAuthUserFromCache(queryClient);
          const resolvedOrgId = updatedUser?.selectedOrganizationId;
          if (resolvedOrgId == null || resolvedOrgId === '') {
            return;
          }

          const org = getOrganizationFromCache(queryClient, resolvedOrgId);
          const billing = await fetchOrganizationBillingImperative(queryClient, resolvedOrgId);
          if (org != null && billing != null) {
            groupPostHogWorkspace(org, billing);
          }
        } catch (err) {
          captureException(
            err,
            {
              file: 'shared/user.ts',
              method: 'updateAuthStorageData',
              infoMessage: 'Failed to set PostHog workspace group',
            },
            trackError
          );
        }
      })();
    });
}

export function getUserDefaults<
  T extends {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    language?: string | null;
  },
>(user?: T | null, emptyName?: boolean) {
  let firstName = user?.firstName ?? '';
  if (!emptyName && (firstName.length === 0 || firstName === '-')) {
    firstName = tGlobal('you');
  }

  const lastName = user?.lastName == null || user?.lastName === '-' ? '' : user.lastName;

  return {
    ...(user ?? {}),
    firstName,
    lastName,
    email: user?.email ?? '',
    language: user?.language ?? 'en',
  };
}

export function layoutUserName(
  user?: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    language?: string | null;
  } | null,
  includeLastName?: boolean
) {
  const { firstName, lastName } = getUserDefaults(user, false);
  if (!includeLastName || lastName == null || lastName === '-' || lastName === '') {
    return firstName;
  }

  return `${firstName} ${lastName}`;
}
